import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Text,
  FormRow,
  Input
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';

import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import { CreateStartAppResponse } from '../api/govos.types';

interface AddZustaendigeStelleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newPartnerId: number) => void;
}

type FormData = {
  name: string;
};

export function AddZustaendigeStelleModal({
  isOpen,
  onClose,
  onSuccess
}: AddZustaendigeStelleModalProps) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const CustomJoi = createJoiWithTranslations(t);

  const { control, handleSubmit, reset, formState, trigger } =
    useForm<FormData>({
      defaultValues: {
        name: ''
      },
      resolver: joiResolver(
        CustomJoi.object<FormData>({
          name: CustomJoi.string()
            .required()
            .messages({
              'string.empty': t(
                'AddZustaendigeStelleModal.validation.nameRequired'
              )
            })
        })
      ),
      mode: 'onTouched'
    });

  const { mutateAsync: createZustaendigeStelle } = useMutation({
    mutationFn: async (data: FormData) => {
      const url = GovosApiRoutes.createStartApp();
      return (await notifyUserFromRequest(
        () =>
          govosClient.post(url, {
            title: data.name
          }),
        {
          successMessage: t('api.success.createZustaendigeStelle'),
          errorMessage: t('api.error.createZustaendigeStelle'),
          loadingMessage: t('api.loading.createZustaendigeStelle')
        }
      )) as unknown as CreateStartAppResponse;
    },
    onSuccess: (response) => {
      const newPartnerId = response.appiid;
      queryClient
        .refetchQueries({
          queryKey: [GovosApiRoutes.startApps()]
        })
        .then(() => {
          onSuccess(newPartnerId);
          reset();
        });
    },
    onError: () => {
      // Error is handled by notifyUserFromRequest
    }
  });

  const handleAdd = async () => {
    const isValid = await trigger();
    if (isValid) {
      handleSubmit((data) => {
        createZustaendigeStelle(data);
      })();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('AddZustaendigeStelleModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <FormRow
          label={t('AddZustaendigeStelleModal.nameLabel')}
          validationMessage={formState.errors.name?.message}
          variant={formState.errors.name ? 'error' : undefined}
        >
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t('AddZustaendigeStelleModal.namePlaceholder')}
              />
            )}
          />
        </FormRow>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button
            variant="primary"
            onClick={() => void handleAdd()}
            disabled={!formState.isValid}
            style={{ width: '100%' }}
          >
            {t('AddZustaendigeStelleModal.addButton')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={handleClose}
            style={{ width: '100%' }}
          >
            {t('AddZustaendigeStelleModal.cancelButton')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
